import { useEffect, useRef } from 'react';

interface PrewarmConfig {
  video?: boolean | MediaTrackConstraints;
  audio?: boolean | MediaTrackConstraints;
}

// Global cache to store prewarmed stream
let globalPrewarmStream: MediaStream | null = null;
let prewarmPromise: Promise<MediaStream | null> | null = null;

/**
 * Pre-warms media devices to reduce initial connection delay
 * Call this early in your app (e.g., on prejoin screen)
 */
export function useMediaPrewarm(config: PrewarmConfig = { video: true, audio: true }) {
  const hasPrewarmedRef = useRef(false);

  useEffect(() => {
    if (hasPrewarmedRef.current || prewarmPromise) return;
    hasPrewarmedRef.current = true;

    const prewarmMedia = async () => {
      try {
        const constraints: MediaStreamConstraints = {
          video: config.video || false,
          audio: config.audio || false,
        };

        console.log('🔥 Pre-warming media devices...');
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        globalPrewarmStream = stream;
        console.log('✅ Media devices pre-warmed successfully');
        return stream;
      } catch (error) {
        console.warn('⚠️ Media prewarm failed:', error);
        return null;
      }
    };

    // Small delay to not block initial render
    setTimeout(() => {
      prewarmPromise = prewarmMedia();
    }, 300);

    return () => {
      // Don't cleanup on unmount - we want to keep the stream for the call
    };
  }, [config.video, config.audio]);
}

/**
 * Consumes the prewarmed stream or creates a new one.
 * Applies video constraints to the prewarmed stream so it matches the
 * requested resolution/aspectRatio (prewarm uses bare {video:true}).
 */
export async function consumePrewarmStream(
  constraints: MediaStreamConstraints
): Promise<MediaStream> {
  let stream: MediaStream | null = null;

  // If we have a prewarm in progress, wait for it
  if (prewarmPromise) {
    const prewarmed = await prewarmPromise;
    if (prewarmed) {
      stream = globalPrewarmStream;
      globalPrewarmStream = null;
      prewarmPromise = null;
      console.log('♻️ Reusing prewarmed stream');
    }
  }

  // If we have a cached stream, use it
  if (!stream && globalPrewarmStream) {
    stream = globalPrewarmStream;
    globalPrewarmStream = null;
    console.log('♻️ Reusing cached prewarmed stream');
  }

  if (stream) {
    // Apply the actual video constraints to the prewarmed track so
    // resolution/aspectRatio match what the caller requested
    const videoConstraints = constraints.video;
    if (videoConstraints && typeof videoConstraints === 'object') {
      const vt = stream.getVideoTracks()[0];
      if (vt) {
        try {
          await vt.applyConstraints(videoConstraints);
          console.log('✅ Applied video constraints to prewarmed track:', vt.getSettings());
        } catch (e) {
          console.warn('⚠️ Failed to apply constraints to prewarmed track, creating new stream:', e);
          // Stop prewarmed stream and fall through to create a fresh one
          stream.getTracks().forEach(t => t.stop());
          stream = null;
        }
      }
    }
  }

  if (stream) return stream;

  // Otherwise create new stream
  console.log('🎥 Creating new media stream');
  return navigator.mediaDevices.getUserMedia(constraints);
}

/**
 * Clears the prewarmed stream (call on unmount/cleanup)
 */
export function clearPrewarmStream() {
  if (globalPrewarmStream) {
    globalPrewarmStream.getTracks().forEach(track => track.stop());
    globalPrewarmStream = null;
  }
  prewarmPromise = null;
}